// Libraries
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useParams, generatePath, Link } from "react-router-dom";
import { Clock, ShieldTick, FileSearch02 } from "@untitled-ui/icons-react";

// Resources
import { pathname } from "#src/config/pathname";

// Components
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import { ButtonV2 } from "#src/commonV2/Button";
import { useAppSelector } from "#src/store/hooks";
import { ContentSection } from "#src/layouts/content";
import ConfigSectionWrapperV2 from "#src/commonV2/helper/wrapper/ConfigSection";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import { FormInput } from "#src/commonV2/form/Input/FormInput";
import { DateTime } from "luxon";
import i18next from "i18next";
import { LoadingStateV2 } from "#src/commonV2/LoadingState";
import { FormDropdown } from "#src/commonV2/form/Dropdown/FormDropdown";

/** Account Setting page. This page actually don't let user change anything. User must go to idcystack to change their profile. */
const AccountSettingPage = () => {
  const { t } = useTranslation("accountSettings");
  const { t: sidebarT } = useTranslation("layout", {
    keyPrefix: "sidebar.v2",
  });
  const { t: commonT } = useTranslation("common", {
    keyPrefix: "languages",
  });

  const { workspaceId } = useParams<"workspaceId">();
  const userInfo = useAppSelector((state) => state.auth.userInfo);

  const locale = i18next.language;

  const country: IFilterItemV2 = useMemo(() => {
    return {
      key: "value",
      value: userInfo?.country,
      getLabel: () => {
        if (userInfo?.country === "vi") {
          return commonT("vietnamese");
        }
        return commonT("english");
      },
    };
  }, [userInfo?.country, commonT]);

  return (
    <div className="flex flex-col pt-5 pb-6 gap-5 overflow-x-hidden overflow-y-auto h-full">
      {/* Header (Breadcrumb + title) */}
      <div className="px-8 flex flex-col gap-4">
        <BreadcrumbV2
          levels={[
            {
              name: sidebarT("accountSettings.label"),
              path: generatePath(pathname.ACCOUNT_SETTINGS, {
                workspaceId,
              }),
            },
          ]}
          currentPage={{
            name: sidebarT("accountSettings.personalDetails"),
          }}
        />
        <span className="text-display-xs font-semibold text-gray-v2-900">
          {t("title")}
        </span>
      </div>
      {/* Header (Breadcrumb + title) end */}

      {/* Personal details */}
      {userInfo ? (
        <ContentSection className="px-0">
          <div className="grid grid-cols-3 gap-8 px-8">
            <div className="col-span-2">
              <ConfigSectionWrapperV2
                icon={<FileSearch02 className="h-5 w-5 text-gray-v2-500" />}
                headerTitle={t("title")}
              >
                <div className="grid grid-cols-2 gap-8 p-4">
                  <div className="flex flex-col gap-6">
                    <FormInput
                      label={t("userInfo.fullName")}
                      value={userInfo.full_name}
                      disabled
                      onChange={() => {}}
                    />
                    <FormInput
                      label={t("userInfo.username")}
                      value={userInfo.username}
                      disabled
                      onChange={() => {}}
                    />
                    <FormInput
                      label={t("userInfo.biography")}
                      value={"---"}
                      disabled
                      onChange={() => {}}
                    />
                    <FormDropdown
                      label={t("userInfo.country")}
                      selected={country}
                      options={[]}
                      disabled
                      onChangeSelected={() => {}}
                    />
                  </div>
                  <div className="flex flex-col gap-6">
                    <FormInput
                      label={t("userInfo.phoneNumber")}
                      value={userInfo.phone}
                      disabled
                      onChange={() => {}}
                    />
                    <FormInput
                      label={t("userInfo.email")}
                      value={userInfo.email}
                      disabled
                      onChange={() => {}}
                    />
                    <FormInput
                      label={t("userInfo.socialProfile")}
                      value={"---"}
                      disabled
                      onChange={() => {}}
                    />
                  </div>
                </div>
              </ConfigSectionWrapperV2>
            </div>
            <div className="flex flex-col items-center gap-6">
              <div className="p-3 rounded-full flex items-center justify-center border border-gray-v2-200 bg-white">
                <img
                  src={userInfo.avatar}
                  className="rounded-full object-contain h-24 w-24"
                />
              </div>
              <span className="flex items-center gap-2 font-regular text-sm text-gray-v2-600">
                <Clock className="h-4 w-4" />
                {t("userInfo.joined")}{" "}
                {DateTime.fromSeconds(userInfo.registered_time).toRelative({
                  locale,
                })}
              </span>
              <Link
                to={"https://id.cystack.net"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
              >
                <ButtonV2 variant="secondary" className="w-full">
                  <ShieldTick className="h-5 w-5" />
                  {t("button.editSecuritySettings")}
                </ButtonV2>
              </Link>
            </div>
          </div>
        </ContentSection>
      ) : (
        <LoadingStateV2 />
      )}
      {/* Personal details end */}
    </div>
  );
};

export default AccountSettingPage;
